import React, { useState, useEffect } from 'react';
import { useAttendance } from '../context/AttendanceContext';
import { useSelector } from 'react-redux';
import configServ from '../services/config';
import configService from '../services/config';
import toast from 'react-hot-toast';
import LeaveTable from './LeaveTable';
import './Leave.css';

const STATUS_COLORS = {
  Pending:  { bg: 'rgba(245,166,35,0.1)',  color: '#f5a623', border: 'rgba(245,166,35,0.25)' },
  Approved: { bg: 'rgba(34,211,165,0.1)',  color: '#22d3a5', border: 'rgba(34,211,165,0.25)' },
  Rejected: { bg: 'rgba(242,95,92,0.1)',   color: '#f25f5c', border: 'rgba(242,95,92,0.25)' },
};

export default function Leave() {
  const { leaves, applyLeave } = useAttendance();
  const { company_id, userId } = useSelector(state => state.globalVariables);

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    leave_type_id: '',
    from_date: '',
    to_date: '',
    reason: '',
    leave_list_id: '',
  });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [leaveList, setLeaveList] = useState([]);
  const [isChanged, setIsChanged] = useState(false);

  // ✅ Min date — 1 month back
  const oneMonthBack = (() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d.toISOString().split('T')[0];
  })();

  const selectedLeaveObj = leaveTypes.find(t => Number(t.id) === Number(form.leave_type_id));
  const isRH = selectedLeaveObj?.leave_code === 'RH';
  const availableBalance = Number(selectedLeaveObj?.balance ?? 0);

  const fetchLeaveData = async () => {
    try {
      const [typesRes, holidaysRes] = await Promise.all([
        configServ.getleaveTypes({
          company_id,
          customer_id: userId,
        }),
        configServ.getActiveGlobalHolidays(company_id),
      ]);
      if (typesRes?.status) setLeaveTypes(typesRes.data || []);
      if (holidaysRes?.status) setLeaveList(holidaysRes.data || []);
    } catch (error) {
      console.error('Error fetching leave data:', error);
    }
  };

  useEffect(() => {
    if (showForm) fetchLeaveData();
  }, [showForm]);

  const handleTypeChange = (e) => {
    const val = e.target.value;
    setForm({ ...form, leave_type_id: val, from_date: '', to_date: '', leave_list_id: '' });
    setErrors({});
  };

  const handleHolidayChange = (e) => {
    const holidayId = e.target.value;
    const selectedHoliday = leaveList.find(h => h.id == holidayId);
    const hDate = selectedHoliday?.holiday_date ? selectedHoliday.holiday_date.split('T')[0] : '';

    setForm({
      ...form,
      leave_list_id: holidayId,
      from_date: hDate,
      to_date: hDate,
    });
  };

  const daysBetween = (from, to) => {
    if (!from || !to) return 0;
    const d = (new Date(to) - new Date(from)) / 86400000 + 1;
    return d > 0 ? Math.ceil(d) : 0;
  };

  const requestedDays = form.from_date && form.to_date
    ? (isRH ? 1 : daysBetween(form.from_date, form.to_date))
    : 0;

  const validate = () => {
    const e = {};
    if (!form.leave_type_id) e.leave_type_id = 'Leave type is required';

    if (isRH) {
      if (!form.leave_list_id) e.leave_list_id = 'Please select a holiday';
    } else {
      if (!form.from_date) e.from_date = 'Start date is required';
      if (!form.to_date) e.to_date = 'End date is required';
      else if (form.to_date < form.from_date) e.to_date = 'End date must be after start date';
    }

    // 1 month back check
    if (form.from_date && form.from_date < oneMonthBack) {
      e.from_date = 'Leave can be applied only up to 1 month back';
    }
    if (form.to_date && form.to_date < oneMonthBack) {
      e.to_date = 'Leave can be applied only up to 1 month back';
    }

    // Balance check
    if (form.leave_type_id && requestedDays > availableBalance) {
      e.to_date = `Only ${availableBalance} day(s) available for this leave type`;
    }

    if (!form.reason.trim()) e.reason = 'Reason is required';
    else if (form.reason.trim().length < 10) e.reason = 'Min 10 characters required';

    return e;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }

    const payload = {
      company_id,
      employee_id: userId,
      ...form,
    };

    try {
      const response = await configServ.createLeave(payload);

      if (response?.status) {
        toast.success('Leave application submitted successfully');
        setSubmitted(true);
        setIsChanged(prev => !prev);

        setTimeout(() => {
          setSubmitted(false);
          setShowForm(false);
          setForm({ leave_type_id: '', from_date: '', to_date: '', reason: '', leave_list_id: '' });
          setErrors({});
        }, 1500);
      } else {
        toast.error(response?.message || 'Failed to submit leave');
      }
    } catch (error) {
      const msg = error?.response?.data?.message || 'Something went wrong. Please try again.';
      toast.error(msg);
    }
  };

  const fmt = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

  return (
    <div className="leave-root fade-in-up" style={{ width: '100%' }}>
      <div className="leave-header-row">
        <div>
          <h2 className="leave-title">My Leave Requests</h2>
        </div>
        <button className="leave-apply-btn" onClick={() => setShowForm(v => !v)}>
          {showForm ? 'Cancel' : 'Apply for Leave'}
        </button>
      </div>

      {showForm && (
        <div
          className="leave-form-card page-card fade-in-up"
          style={{
            backgroundColor: '#ffffff',
            color: '#1e293b',
            padding: '24px',
            borderRadius: '16px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 10px 25px rgba(0,0,0,0.05)',
          }}
        >
          <p className="page-section-title" style={{ color: '#1e293b', fontWeight: 700, marginBottom: '8px', fontSize: '1.1rem' }}>
            New Leave Application
          </p>

          {/* ✅ Hint */}
          <p
            style={{
              fontSize: '0.8rem',
              color: '#64748b',
              marginBottom: 16,
              padding: '8px 12px',
              background: '#f1f5f9',
              borderRadius: 8,
            }}
          >
            📅 You can apply for leave up to <strong>1 month back</strong>, or any future date.
          </p>

          {leaveTypes.length === 0 && (
            <div style={{
              padding: '12px 16px',
              background: 'rgba(242,95,92,0.08)',
              border: '1px solid rgba(242,95,92,0.25)',
              color: '#f25f5c',
              borderRadius: '10px',
              marginBottom: '16px',
              fontSize: '0.9rem',
            }}>
              No leave types have been assigned to you. Please contact HR.
            </div>
          )}

          <form onSubmit={handleSubmit} className="leave-form" noValidate>
            <div className="leave-form-grid">
              <div className={`leave-field ${errors.leave_type_id ? 'has-error' : ''}`}>
                <label className="leave-label">Leave Type</label>
                <select
                  className="leave-select"
                  value={form.leave_type_id}
                  onChange={handleTypeChange}
                  disabled={leaveTypes.length === 0}
                >
                  <option value="">Select Type</option>
                  {leaveTypes.map(t => (
                    <option key={t.id} value={t.id}>
                      {t.leave_name} ({t.balance} day{t.balance !== 1 ? 's' : ''} left)
                    </option>
                  ))}
                </select>
                {errors.leave_type_id && <span className="leave-err">{errors.leave_type_id}</span>}
                {form.leave_type_id && (
                  <span style={{ fontSize: '0.8rem', color: '#64748b', marginTop: 4 }}>
                    Available: <strong>{availableBalance}</strong> day(s)
                  </span>
                )}
              </div>

              {isRH ? (
                <div className={`leave-field ${errors.leave_list_id ? 'has-error' : ''}`}>
                  <label className="leave-label">Select Holiday</label>
                  <select className="leave-select" value={form.leave_list_id} onChange={handleHolidayChange}>
                    <option value="">Choose Restricted Holiday</option>
                    {leaveList.map(h => (
                      <option key={h.id} value={h.id}>
                        {h.title} ({fmt(h.holiday_date)})
                      </option>
                    ))}
                  </select>
                  {errors.leave_list_id && <span className="leave-err">{errors.leave_list_id}</span>}
                </div>
              ) : (
                <div className={`leave-field ${errors.from_date ? 'has-error' : ''}`}>
                  <label className="leave-label">From Date</label>
                  <input
                    type="date"
                    className="leave-input"
                    min={oneMonthBack}   /* ✅ 1 month back */
                    /* No max — future allowed */
                    value={form.from_date}
                    onChange={e => setForm(f => ({ ...f, from_date: e.target.value }))}
                  />
                  {errors.from_date && <span className="leave-err">{errors.from_date}</span>}
                </div>
              )}

              <div className={`leave-field ${errors.to_date ? 'has-error' : ''}`}>
                <label className="leave-label">To Date</label>
                <input
                  type="date"
                  className="leave-input"
                  min={form.from_date || oneMonthBack}   /* ✅ 1 month back ya from_date */
                  /* No max — future allowed */
                  disabled={isRH}
                  value={form.to_date}
                  onChange={e => setForm(f => ({ ...f, to_date: e.target.value }))}
                />
                {errors.to_date && <span className="leave-err">{errors.to_date}</span>}
              </div>
            </div>

            {form.from_date && form.to_date && (
              <div className="leave-days-pill">
                {requestedDays} day{requestedDays > 1 ? 's' : ''} selected
                {form.leave_type_id && (
                  <> · {Math.max(0, availableBalance - requestedDays)} day(s) will remain</>
                )}
              </div>
            )}

            <div className={`leave-field ${errors.reason ? 'has-error' : ''}`}>
              <label className="leave-label">Reason</label>
              <textarea
                className="leave-textarea"
                rows={3}
                placeholder="Describe the reason for your leave..."
                value={form.reason}
                onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}
              />
              {errors.reason && <span className="leave-err">{errors.reason}</span>}
            </div>

            <div className="leave-form-actions">
              <button
                type="submit"
                className={`leave-submit-btn ${submitted ? 'done' : ''}`}
                disabled={submitted || leaveTypes.length === 0}
              >
                {submitted ? 'Submitted!' : 'Submit Application'}
              </button>
            </div>
          </form>
        </div>
      )}

      <LeaveTable ischange={isChanged} />
    </div>
  );
}